<script setup lang="ts">
/**
 * OffenseStats — DPS breakdown: turrets, missiles, drones, fighters, total
 */
import type { OffenseStats as OffenseStatsType } from '../../../shared/types';

const props = defineProps<{
  offense: OffenseStatsType;
}>();

function fmt(n: number): string {
  if (n === 0) return '0';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toFixed(1);
}
</script>

<template>
  <div class="offense-stats">
    <!-- Total DPS highlight -->
    <div class="total-dps-row">
      <span class="dps-label">Total DPS</span>
      <span class="dps-total">{{ fmt(props.offense.total.dps) }}</span>
    </div>

    <!-- Alpha strike -->
    <div class="alpha-row" v-if="props.offense.total.alpha && props.offense.total.alpha > 0">
      <span class="alpha-label">Alpha Strike</span>
      <span class="alpha-value">{{ fmt(props.offense.total.alpha) }}</span>
    </div>

    <!-- Per-system breakdown -->
    <div class="system-breakdown">
      <div v-if="props.offense.turret.dps > 0" class="system-row">
        <span class="system-label">Turrets</span>
        <span class="system-value">{{ fmt(props.offense.turret.dps) }} DPS</span>
      </div>
      <div v-if="props.offense.missile.dps > 0" class="system-row">
        <span class="system-label">Missiles</span>
        <span class="system-value">{{ fmt(props.offense.missile.dps) }} DPS</span>
      </div>
      <div v-if="props.offense.drone.dps > 0" class="system-row">
        <span class="system-label">Drones</span>
        <span class="system-value">{{ fmt(props.offense.drone.dps) }} DPS</span>
      </div>
      <div v-if="props.offense.fighter.dps > 0" class="system-row">
        <span class="system-label">Fighters</span>
        <span class="system-value">{{ fmt(props.offense.fighter.dps) }} DPS</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.offense-stats {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.total-dps-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
}

.dps-label {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.dps-total {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--accent-red);
  font-variant-numeric: tabular-nums;
}

.alpha-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1px 0;
}

.alpha-label {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.alpha-value {
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}

.system-breakdown {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding-top: 2px;
}

.system-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1px 0;
}

.system-label {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.system-value {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}
</style>
